import { AuthError, VonageError } from '@/lib/authentication';
import { getNetworkStatus } from '@/lib/network-status';
import { isDesktop } from '@/lib/platform';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import { newRelicService } from '@/services/newrelic';
import * as Sentry from '@sentry/react';

declare global {
  interface Window {
    __appStartTime?: number;
  }
}

const MAX_MESSAGE_LENGTH = 300;

// Strip phone numbers / prefixed ids / uuids so the route is PII-safe for
// product analytics (New Relic keeps the full currentUrl via its agent).
const sanitizePathSegment = (segment: string): string => {
  if (!segment) {
    return segment;
  }
  if (/^\+?\d{7,}$/.test(segment)) {
    return ':phone';
  }
  if (/^[a-z]{2,6}[-_][A-Za-z0-9_-]{6,}$/.test(segment)) {
    return `:${segment.split(/[-_]/)[0]}`;
  }
  if (/^[0-9a-f-]{16,}$/i.test(segment)) {
    return ':id';
  }
  return segment;
};

const sanitizePath = (pathname: string): string =>
  pathname.split('/').map(sanitizePathSegment).join('/');

type SplashErrorContext = {
  /** True when the splash mounted directly in error mode (cold-boot dead-end). */
  bootedInError: boolean;
};

/**
 * Diagnostics for the "We can't reach our servers" splash. Primary signal is
 * the New Relic PageAction — its agent harvests on unload, so it survives the
 * Retry reload that drops Sentry's fire-and-forget transport.
 */
export const reportSplashError = (
  error: Error | boolean | unknown,
  { bootedInError }: SplashErrorContext,
): void => {
  const isErrorInstance = error instanceof Error;
  const net = getNetworkStatus();
  const platform = isDesktop() ? 'electron' : 'web';
  const timeSinceAppStartMs = Date.now() - (window.__appStartTime ?? Date.now());

  // openapi-fetch throws the parsed response body (not an Error) on HTTP errors,
  // so `code` distinguishes a server status error from a network-drop TypeError.
  const apiCode =
    typeof error === 'object' && error !== null && 'code' in error
      ? String((error as { code: unknown }).code)
      : undefined;

  const errorName = isErrorInstance ? error.constructor.name : typeof error;
  const errorMessage = isErrorInstance ? error.message.slice(0, MAX_MESSAGE_LENGTH) : undefined;
  const errorCause =
    isErrorInstance && error.cause instanceof Error
      ? error.cause.message.slice(0, MAX_MESSAGE_LENGTH)
      : undefined;
  const isAuthError = error instanceof AuthError;
  const isVonageError = error instanceof VonageError;

  newRelicService.addPageAction('app:splash:error_shown', {
    errorName,
    errorMessage,
    errorCause,
    apiCode,
    isErrorInstance,
    isAuthError,
    isVonageError,
    bootedInError,
    online: net.isOnline,
    networkQuality: net.quality,
    connectionType: net.connectionType,
    downlink: net.downlink,
    rtt: net.rtt,
    platform,
    pathname: window.location.pathname,
    timeSinceAppStartMs,
  });

  analytics.track(ANALYTICS_EVENTS.APP.SPLASH_ERROR_SHOWN, {
    error_name: errorName,
    error_message: errorMessage,
    api_code: apiCode,
    is_error_instance: isErrorInstance,
    is_auth_error: isAuthError,
    is_vonage_error: isVonageError,
    booted_in_error: bootedInError,
    online: net.isOnline,
    network_quality: net.quality,
    connection_type: net.connectionType,
    platform,
    path: sanitizePath(window.location.pathname),
    time_since_app_start_ms: timeSinceAppStartMs,
  });

  if (isErrorInstance) {
    Sentry.captureException(error, {
      tags: { component: 'splashscreen_user_visible_error' },
      extra: {
        errorType: error.constructor.name,
        errorMessage: error.message,
        errorCause,
        apiCode,
        isAuthError,
        isVonageError,
        bootedInError,
        online: net.isOnline,
        networkQuality: net.quality,
        connectionType: net.connectionType,
        timeSinceAppStartMs,
      },
    });
  }
};
